/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { memo, useContext } from 'react';
import DetailSummaryComponent from '../../../shared/DetailSummaryComponent/DetailSummaryComponent';
import '../../../../scss/ViewDetailsTheme.scss';
import {
  getConvertedValue,
  getConvertedNullValue,
  getMaxConvertedValueFromArray
} from '../../../shared/Utils/unitHandling/unitHandling';
import { ConversionType } from '../../../shared/Utils/unitHandling/UnitModel';
import { convertChartPremiumToBasic } from '../../../shared/Utils/EnergyUsagesViewDetailsUtils';
import descriptors from '../../../../locales/en-US/descriptors';
import { useTranslation } from '@scuf/localization';
import TrendChart from '../../../shared/TrendChart/TrendChart';
import { EnergyUsageField } from '../../../shared/Enums';
import {
  IEmaasEnergyUsageComponentData,
  IAny
} from '../../../DashboardKPIWidgets/IEnergyManagementProps';
import { ApiContext } from '../../../../context/ApiContext';
import { UnitUtility } from '@bmsp/bmsp.web.component.common';
import {
  PERCENT_CHANGE_THRESHOLD,
  PreferredUnit
} from '../../../shared/Utils/Constants';

export interface IEnergyUsagesAllUtilityDetailsComponentProps {
  data: IEmaasEnergyUsageComponentData;
  checkAllSitesAndMonthData?: boolean;
  dataSourceType?: string;
}
const EmaasEUsageAllUtilDetailsBasicComponent = (
  props: IEnergyUsagesAllUtilityDetailsComponentProps
) => {
  const d = descriptors.energyManagement;
  const [t, lng] = useTranslation();
  const { data, checkAllSitesAndMonthData } = props;
  const apiContext = useContext(ApiContext);
  const { userPreferedUnit } = apiContext;

  let fromUnit;
  // if (true) {
  //   fromUnit = userPreferedUnit as any;
  // } else {
  //   fromUnit = energyMeasurementUnit;
  // }

  fromUnit =
    userPreferedUnit === 'megajoule'
      ? userPreferedUnit
      : props.data.sourceType && props.data.sourceType.toLowerCase() === 'gas'
      ? apiContext.toggleEnableKwhGas
        ? PreferredUnit.Electricity
        : PreferredUnit.Gas
      : (PreferredUnit.Electricity as any);
  const {
    monthWiseAggregations,
    energyMeasurementUnit,
    summary,
    sourceType,
    totalSiteCount
  } = data;
  const {
    totalBaselineConsumption,
    totalConsumption,
    totalEnergyDeviation,
    totalEnergyDeviationPercentage
  } = summary;
  const valuesArray = [totalConsumption, totalBaselineConsumption];

  let conversionType = ConversionType.ELECTRICITY;
  if (sourceType == 'Gas') {
    conversionType = ConversionType.GAS;
  } else if (sourceType == 'Water') {
    conversionType = ConversionType.WATER;
  }

  const maxValueUnitConversion = getMaxConvertedValueFromArray(
    conversionType,
    conversionType === ConversionType.ELECTRICITY
      ? fromUnit
      : energyMeasurementUnit,
    valuesArray,
    energyMeasurementUnit
  );

  const aggregationSummaryData = {
    actualCost: getConvertedValue(
      conversionType,
      energyMeasurementUnit,
      totalConsumption,
      getMaxConvertedValueFromArray(
        conversionType,
        conversionType === ConversionType.ELECTRICITY
          ? fromUnit
          : energyMeasurementUnit,
        [totalConsumption],
        energyMeasurementUnit
      ).Unit
    ).Value,
    actualUnit: getMaxConvertedValueFromArray(
      conversionType,
      conversionType === ConversionType.ELECTRICITY
        ? fromUnit
        : energyMeasurementUnit,
      [totalConsumption],
      energyMeasurementUnit
    ).Unit,
    baselineCost: getConvertedValue(
      conversionType,
      energyMeasurementUnit,
      totalBaselineConsumption,
      getMaxConvertedValueFromArray(
        conversionType,
        conversionType === ConversionType.ELECTRICITY
          ? fromUnit
          : energyMeasurementUnit,
        [totalBaselineConsumption],
        energyMeasurementUnit
      ).Unit
    ).Value,
    baselineUnit: getMaxConvertedValueFromArray(
      conversionType,
      conversionType === ConversionType.ELECTRICITY
        ? fromUnit
        : energyMeasurementUnit,
      [totalBaselineConsumption],
      energyMeasurementUnit
    ).Unit,
    costSavings:
      apiContext.showChangeWhenData &&
      (isNaN(Number(totalConsumption)) ||
        isNaN(Number(totalBaselineConsumption)) ||
        Number(totalConsumption) === 0 ||
        Number(totalBaselineConsumption) === 0 ||
        !checkAllSitesAndMonthData ||
        Math.abs(Number(totalEnergyDeviationPercentage)) >
          PERCENT_CHANGE_THRESHOLD)
        ? null
        : getConvertedValue(
            conversionType,
            energyMeasurementUnit,
            totalEnergyDeviation,
            getMaxConvertedValueFromArray(
              conversionType,
              conversionType === ConversionType.ELECTRICITY
                ? fromUnit
                : energyMeasurementUnit,
              [totalEnergyDeviation],
              energyMeasurementUnit
            ).Unit
          ).Value,
    changeUnit: getMaxConvertedValueFromArray(
      conversionType,
      conversionType === ConversionType.ELECTRICITY
        ? fromUnit
        : energyMeasurementUnit,
      [totalEnergyDeviation],
      energyMeasurementUnit
    ).Unit,
    costSavingsPercentage: !checkAllSitesAndMonthData
      ? null
      : getConvertedNullValue(totalEnergyDeviationPercentage)
  };
  const basicGraphData = convertChartPremiumToBasic(
    monthWiseAggregations,
    fromUnit, //energyMeasurementUnit
    conversionType,
    energyMeasurementUnit,
    lng
  );

  const legendItems = [
    {
      text: t(d.PREVIOUSPERIOD),
      color: '#606061',
      className: 'emaas-legend-item-bar-baseline',
      type: 'Bar'
    },
    {
      text: t(d.ACTUAL),
      color: '#1272B2',
      className: 'emaas-legend-item-bar-actual',
      type: 'Bar'
    }
  ];
  let deviationScaledData;
  if (aggregationSummaryData.costSavings === 0 && totalEnergyDeviation !== 0) {
    deviationScaledData = getConvertedValue(
      conversionType,
      energyMeasurementUnit,
      totalEnergyDeviation,
      maxValueUnitConversion.Unit
    );
  }
  return (
    <div className="allUtility-performance-detail-container">
      <div>
        <DetailSummaryComponent
          aggregationData={aggregationSummaryData}
          measurementUnit={maxValueUnitConversion.Unit}
          detailType="detailSummaryWithoutCurrency"
          deviationScaledData={deviationScaledData}
          fromCarbonScopeOne={true}
        />
        <TrendChart
          measurementUnit={basicGraphData.chartUnit}
          chartData={{ ...basicGraphData, totalSiteCount }}
          legendItems={legendItems}
          category={EnergyUsageField.ENERGY_USAGE}
          showAllTooltips={true}
          dataSourceType={props.dataSourceType}
        />
      </div>
    </div>
  );
};

export default memo(EmaasEUsageAllUtilDetailsBasicComponent);
